import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import "./ConfirmModal.css";

const ConfirmContext = createContext(null);
const NotifyContext = createContext(null);

const TOAST_DURATION_MS = 20000;
const MAX_TOASTS = 4;

function inferToastType(message) {
  const m = String(message || "").toLowerCase();
  if (
    /success|created|updated|saved|deleted|deactivated|added|removed|sent|complete/.test(
      m
    )
  ) {
    return "success";
  }
  if (
    /error|fail|invalid|unable|could not|please|required|denied|not found|missing|forbidden/.test(
      m
    )
  ) {
    return "error";
  }
  return "info";
}

function ToastIcon({ type }) {
  if (type === "success") return <FaCheckCircle aria-hidden />;
  if (type === "error") return <FaExclamationCircle aria-hidden />;
  return <FaInfoCircle aria-hidden />;
}

/**
 * Promise-based confirm dialog + rich toast notifications for admin screens.
 * Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete", message: "…" }))) return;
 *   const notify = useNotify();
 *   notify.success("Saved");
 */
export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [toasts, setToasts] = useState([]);
  const resolveRef = useRef(null);
  const toastIdRef = useRef(0);
  const timersRef = useRef(new Map());

  const dismissToast = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    (type, message, durationMs = TOAST_DURATION_MS) => {
      const text = message == null ? "" : String(message);
      if (!text.trim()) return;
      const id = ++toastIdRef.current;
      setToasts((prev) => {
        const next = [...prev, { id, type, message: text }];
        return next.length > MAX_TOASTS ? next.slice(next.length - MAX_TOASTS) : next;
      });
      const timer = setTimeout(() => dismissToast(id), durationMs);
      timersRef.current.set(id, timer);
    },
    [dismissToast]
  );

  const notify = useMemo(
    () => ({
      success: (message, durationMs) => pushToast("success", message, durationMs),
      error: (message, durationMs) => pushToast("error", message, durationMs),
      info: (message, durationMs) => pushToast("info", message, durationMs),
      alert: (message, durationMs) =>
        pushToast(inferToastType(message), message, durationMs),
    }),
    [pushToast]
  );

  const confirm = useCallback((options) => {
    const opts = typeof options === "string" ? { message: options } : options || {};
    return new Promise((resolve) => {
      if (resolveRef.current) {
        resolveRef.current(false);
      }
      resolveRef.current = resolve;
      setDialog({
        title: opts.title || "Confirm",
        message: opts.message || "Are you sure?",
        confirmLabel: opts.confirmLabel || "Yes",
        cancelLabel: opts.cancelLabel || "No",
        danger: opts.danger !== false,
      });
    });
  }, []);

  const finish = useCallback((result) => {
    const resolve = resolveRef.current;
    resolveRef.current = null;
    setDialog(null);
    if (resolve) resolve(result);
  }, []);

  // Bridge native alert() → rich toast (covers all existing alert() call sites)
  useEffect(() => {
    const previousAlert = window.alert;
    window.alert = (message) => {
      pushToast(inferToastType(message), message);
    };
    return () => {
      window.alert = previousAlert;
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, [pushToast]);

  // Preview samples: open any page with ?demoToasts=1
  useEffect(() => {
    try {
      if (!new URLSearchParams(window.location.search).has("demoToasts")) return undefined;
    } catch {
      return undefined;
    }
    const t = setTimeout(() => {
      pushToast("success", "Lead saved successfully.");
      pushToast("error", "Please fill all required fields.");
      pushToast("info", "Lead details copied to clipboard.");
    }, 400);
    return () => clearTimeout(t);
  }, [pushToast]);

  // Escape closes confirm dialog
  useEffect(() => {
    if (!dialog) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        finish(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dialog, finish]);

  return (
    <ConfirmContext.Provider value={confirm}>
      <NotifyContext.Provider value={notify}>
        {children}

        {dialog ? (
          <div
            className="app-confirm-overlay"
            onClick={() => finish(false)}
            role="presentation"
          >
            <div
              className={`app-confirm-modal ${
                dialog.danger ? "app-confirm-modal--danger" : "app-confirm-modal--info"
              }`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="app-confirm-title"
            >
              <div className="app-confirm-header">
                <div className="app-confirm-title-row">
                  <span
                    className={`app-confirm-icon ${
                      dialog.danger
                        ? "app-confirm-icon--danger"
                        : "app-confirm-icon--info"
                    }`}
                    aria-hidden
                  >
                    {dialog.danger ? <FaExclamationTriangle /> : <FaInfoCircle />}
                  </span>
                  <h3 id="app-confirm-title">{dialog.title}</h3>
                </div>
                <button type="button" onClick={() => finish(false)} aria-label="Close">
                  <FaTimes />
                </button>
              </div>
              <div className="app-confirm-body">
                {typeof dialog.message === "string" ? (
                  <p>{dialog.message}</p>
                ) : (
                  dialog.message
                )}
                <div className="app-confirm-actions">
                  <button
                    type="button"
                    className="app-confirm-btn app-confirm-btn--ghost"
                    onClick={() => finish(false)}
                  >
                    {dialog.cancelLabel}
                  </button>
                  <button
                    type="button"
                    className={`app-confirm-btn ${
                      dialog.danger
                        ? "app-confirm-btn--danger"
                        : "app-confirm-btn--primary"
                    }`}
                    onClick={() => finish(true)}
                    autoFocus
                  >
                    {dialog.confirmLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {toasts.length > 0 ? (
          <div className="app-toast-stack" aria-live="polite" aria-relevant="additions">
            {toasts.map((toast) => (
              <div
                key={toast.id}
                className={`app-toast app-toast--${toast.type}`}
                role="status"
              >
                <span className="app-toast-icon" aria-hidden>
                  <ToastIcon type={toast.type} />
                </span>
                <p className="app-toast-message">{toast.message}</p>
                <button
                  type="button"
                  className="app-toast-close"
                  onClick={() => dismissToast(toast.id)}
                  aria-label="Dismiss"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </NotifyContext.Provider>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error("useConfirm must be used within ConfirmProvider");
  }
  return confirm;
}

export function useNotify() {
  const notify = useContext(NotifyContext);
  if (!notify) {
    throw new Error("useNotify must be used within ConfirmProvider");
  }
  return notify;
}

export default ConfirmProvider;
